/**
 * Comprehensive phone number utilities for Ghana
 * Handles all common formats: +233, 233, 0, etc.
 */

export interface PhoneValidationResult {
  isValid: boolean;
  normalizedPhone: string;
  originalPhone: string;
  errorMessage?: string;
}

/**
 * Normalize Ghana phone numbers to a consistent format (+233XXXXXXXXX)
 * Handles various input formats:
 * - +233241234567 -> +233241234567
 * - 233241234567 -> +233241234567  
 * - 0241234567 -> +233241234567
 * - 241234567 -> +233241234567
 */
export const normalizeGhanaPhone = (phone: string): string => {
  if (!phone) return '';

  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');
  
  // Remove + and work with digits only for processing
  const digitsOnly = cleaned.replace(/\+/g, '');
  
  // Handle different formats
  if (digitsOnly.length === 0) return '';

  // If starts with 233 (country code)
  if (digitsOnly.startsWith('233')) {
    if (digitsOnly.length === 12) {
      return `+${digitsOnly}`;
    }
    return ''; // Invalid length
  }

  // If starts with 0 (local format)
  if (digitsOnly.startsWith('0')) {
    if (digitsOnly.length === 10) {
      return `+233${digitsOnly.substring(1)}`;
    }
    return ''; // Invalid length
  }

  // If it's just the 9-digit number (without country code or 0)
  if (digitsOnly.length === 9) {
    return `+233${digitsOnly}`;
  }

  // If it's 10 digits and doesn't start with 0, might be a mistake
  if (digitsOnly.length === 10 && !digitsOnly.startsWith('0')) {
    // Try treating first digit as part of number (common mistake)
    return `+233${digitsOnly}`;
  }

  return ''; // Invalid format
};

/**
 * Validate Ghana phone number and return detailed result
 */
export const validateGhanaPhone = (phone: string): PhoneValidationResult => {
  const originalPhone = phone;
  const normalizedPhone = normalizeGhanaPhone(phone);

  if (!normalizedPhone) {
    return {
      isValid: false,
      normalizedPhone: '',
      originalPhone,
      errorMessage: 'Please enter a valid Ghana phone number (e.g., 0241234567, +233241234567)'
    };
  }

  // Additional validation for Ghana networks
  const networkPrefixes = [
    '20', '21', // Vodafone
    '23', '24', '25', '26', '27', '28', '29', // MTN
    '30', '31', // Airtel-Tigo
    '50', '51', '52', '53', '54', '55', '56', '57', '58', '59', // MTN
    '70', '71', '72', '73', '74', '75', '76', '77', '78', '79' // Various networks
  ];

  const numberPart = normalizedPhone.substring(4, 6); // Extract first 2 digits after +233
  const isValidNetwork = networkPrefixes.includes(numberPart);

  if (!isValidNetwork) {
    return {
      isValid: false,
      normalizedPhone: '',
      originalPhone,
      errorMessage: 'Please enter a valid Ghana mobile network number'
    };
  }

  return {
    isValid: true,
    normalizedPhone,
    originalPhone
  };
};

/**
 * Format phone number for display (user-friendly format)
 */
export const formatPhoneForDisplay = (phone: string): string => {
  const normalized = normalizeGhanaPhone(phone);
  if (!normalized) return phone;

  // Convert +233241234567 to +233 24 123 4567
  const countryCode = normalized.substring(0, 4); // +233
  const firstPart = normalized.substring(4, 6);   // 24
  const secondPart = normalized.substring(6, 9);  // 123
  const thirdPart = normalized.substring(9);      // 4567

  return `${countryCode} ${firstPart} ${secondPart} ${thirdPart}`;
};

/**
 * Generate possible phone number variations for database search
 * Returns array of possible formats a user might have entered
 */
export const generatePhoneVariations = (phone: string): string[] => {
  const normalized = normalizeGhanaPhone(phone);
  if (!normalized) return [phone];

  const variations = new Set<string>();
  
  // Add the normalized version
  variations.add(normalized);
  
  // Add without + prefix
  variations.add(normalized.substring(1));
  
  // Add with 0 prefix (local format)
  const localFormat = `0${normalized.substring(4)}`;
  variations.add(localFormat);
  
  // Add just the 9-digit number
  variations.add(normalized.substring(4));
  
  // Add the original input (in case it was stored differently)
  variations.add(phone);

  return Array.from(variations);
};

/**
 * Compare two phone numbers for equality (handles different formats)
 */
export const arePhoneNumbersEqual = (phone1: string, phone2: string): boolean => {
  const normalized1 = normalizeGhanaPhone(phone1);
  const normalized2 = normalizeGhanaPhone(phone2);
  
  return normalized1 && normalized2 && normalized1 === normalized2;
};

/**
 * Extract phone number from text (useful for SMS parsing)
 */
export const extractPhoneFromText = (text: string): string | null => {
  // Look for patterns that might be phone numbers
  const phonePatterns = [
    /\+233\d{9}/g,           // +233241234567
    /233\d{9}/g,             // 233241234567
    /0\d{9}/g,               // 0241234567
    /\b\d{9}\b/g             // 241234567 (9 digits)
  ];

  for (const pattern of phonePatterns) {
    const matches = text.match(pattern);
    if (matches) {
      for (const match of matches) {
        const validation = validateGhanaPhone(match);
        if (validation.isValid) {
          return validation.normalizedPhone;
        }
      }
    }
  }

  return null;
};

/**
 * Clean phone number input in real-time (for form inputs)
 */
export const cleanPhoneInput = (input: string): string => {
  // Allow digits, +, spaces, parentheses, and dashes for user-friendly input
  return input.replace(/[^\d+\s()-]/g, '');
};

/**
 * Get phone number display name for UI
 */
export const getPhoneDisplayName = (phone: string): string => {
  const validation = validateGhanaPhone(phone);
  if (!validation.isValid) return phone;

  const formatted = formatPhoneForDisplay(validation.normalizedPhone);
  const networkName = getNetworkName(validation.normalizedPhone);
  
  return networkName ? `${formatted} (${networkName})` : formatted;
};

/**
 * Get network name from phone number
 */
export const getNetworkName = (phone: string): string | null => {
  const normalized = normalizeGhanaPhone(phone);
  if (!normalized) return null;

  const prefix = normalized.substring(4, 6);
  
  if (['20', '21'].includes(prefix)) return 'Vodafone';
  if (['23', '24', '25', '26', '27', '28', '29', '50', '51', '52', '53', '54', '55', '56', '57', '58', '59'].includes(prefix)) return 'MTN';
  if (['30', '31'].includes(prefix)) return 'AirtelTigo';
  if (['70', '71', '72', '73', '74', '75', '76', '77', '78', '79'].includes(prefix)) return 'Various';
  
  return null;
};

export default {
  normalizeGhanaPhone,
  validateGhanaPhone,
  formatPhoneForDisplay,
  generatePhoneVariations,
  arePhoneNumbersEqual,
  extractPhoneFromText,
  cleanPhoneInput,
  getPhoneDisplayName,
  getNetworkName
};